import React, { useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";
import { Fetcher } from '../../../Helpers/fetcher';
import { Notify } from '../../../Helpers/notification';
import { Card, Form, Button, InputGroup, Alert, Spinner } from 'react-bootstrap';
import { FaShieldAlt, FaSearch, FaSave, FaInfoCircle } from 'react-icons/fa';
import { normalizePermissions } from '../helpers/normalizePermissions';
import "./index.css";
import { buildAclPayload, buildExpandedModulesFromPermissions, buildModuleListingPayload, hasAnyPermissionSelected, parseAclModuleListingData, sortModules, MANAGE_USERS_SETTING_OPTIONS, NUMBER_ACTION_OPTIONS } from '../helpers/aclHelper';
import AclTable from '../components/AclTable';

const UpdateAcl = ({ app, id }) => {
    const apiPath = app.apiPath;
    const userId = app.userId;
    const history = useHistory();

    const [aclName, setAclName] = useState('');
    const [moduleList, setModuleList] = useState([]);
    const [permissions, setPermissions] = useState({});
    const [search, setSearch] = useState('');
    const [expandedModules, setExpandedModules] = useState({});
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    const [moduleListingData, setModuleListingData] = useState({});
    const [multiSelectOptions, setMultiSelectOptions] = useState({});

    const fetchModuleData = async () => {
        try {
            const dialerUrl = `${apiPath}/api/obd/dialers/unallocated?excludeAclId=${id}`;
            const moduleUrl = `${apiPath}/api/obd/module`;

            const transform = (arr, label, value) =>
                (Array.isArray(arr) ? arr : []).map(item => ({
                    name: item?.[label] ?? "",
                    id: item?.[value] ?? "",
                    dialerName: item?.dialerName ?? ""
                }));

            const locationUrl = `${apiPath}/api/obd/locations/${userId}`;

        const [dialerRes, moduleRes, locationRes] = await Promise.all([
            Fetcher.get(dialerUrl),
            Fetcher.get(moduleUrl),
            Fetcher.get(locationUrl).catch(() => [])
        ]);

        setMultiSelectOptions({
            "Manage Dialers": transform(dialerRes, "dialerName", "dialerId"),
            "Manage Users": [...transform(moduleRes, "moduleName", "moduleId"), ...MANAGE_USERS_SETTING_OPTIONS],
            "Manage Locations": transform(locationRes, "locationName", "locationId"),
            "Number Action": NUMBER_ACTION_OPTIONS
        });

        } catch (err) {
            console.log("fetchModuleData error", err);
            Notify.notify("Error loading dropdown data");
        }
    };

    const getModuleList = async () => {
        setLoading(true);
        try {
            const res = await Fetcher.get(`${apiPath}/api/obd/acl/module/list`);

            const data = (Array.isArray(res) ? res : []).map(m => {
                let children = [];
                try {
                    children = Array.isArray(m.childModule)
                        ? m.childModule
                        : JSON.parse(m.childModule || "[]");
                } catch {
                    children = [];
                }

                return { ...m, children: children.filter(c => c.moduleName !== "Create Location") };
            });

            setModuleList(data);


        } catch {
            Notify.notify("Error loading modules");
        } finally {
            setLoading(false);
        }
    };

    const getAclDetail = async () => {
        setPageLoading(true);
        try {
            const res = await Fetcher.get(
                `${apiPath}/api/obd/get/acl/setting/${id}`
            );

            setAclName(res?.aclName || '');

            const data = res?.moduleOperation
                ? (typeof res.moduleOperation === "string"
                    ? JSON.parse(res.moduleOperation)
                    : res.moduleOperation)
                : {};

            const normalized = normalizePermissions(data);
            setPermissions(normalized);

            const expanded = buildExpandedModulesFromPermissions(data);
            setExpandedModules(expanded);

            const restored = parseAclModuleListingData(res?.moduleListingData);
            setModuleListingData(restored);
        } catch {
            Notify.notify("Failed to load ACL details");
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        const load = async () => {
            await getModuleList();
            await fetchModuleData();
            if (id) {
                await getAclDetail()
            };
        };
        load();
    }, [id]);

    // ---------------- TOGGLES ----------------
    const togglePermission = (id, key) => {
        setPermissions(prev => {
            const updated = {
                ...prev,
                [id]: {
                    ...(prev[id] || {}),
                    [key]: !prev?.[id]?.[key]
                }
            };

            if (key === "view_permission" && !updated[id]?.view_permission) {
                setModuleListingData(prev => {
                    const copy = { ...prev };
                    delete copy[id];
                    return copy;
                });
            }

            return updated;
        });
    };

    const toggleAll = (id, keys, checked) => {
        setPermissions(prev => {
            const updated = { ...prev };
            if (!updated[id]) updated[id] = {};
            keys.forEach(k => updated[id][k] = checked);
            return updated;
        });
    };

    const isAllChecked = (id, keys) => {
        const p = permissions[id] || {};
        return keys.every(k => p[k] === true);
    };

    const toggleExpand = (id) => {
        setExpandedModules(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const filteredModules = moduleList.sort(sortModules).filter(m =>
        m.mname.toLowerCase().includes(search.toLowerCase())
    );

    const handleUpdate = () => {
        if (!aclName.trim()) {
            Notify.notify("ACL Name is required");
            return;
        }

        if (!hasAnyPermissionSelected(moduleList, permissions)) {
            Notify.notify("Please select at least one permission before updating");
            return;
        }
        const cleanedModuleListingData = buildModuleListingPayload(moduleListingData)
        const payload = buildAclPayload({
            moduleList,
            permissions,
            aclName,
            userId,
        });

        Fetcher.post(`${apiPath}/api/obd/update/acl/settings`, {
            method: "POST",
            headers: { "Content-type": "application/json" },
            body: JSON.stringify({
                ...payload,
                id,
                moduleListingData: JSON.stringify(cleanedModuleListingData)
            })
        })
            .then(res => {
                Notify.notify(res.message || "Updated successfully");
                history.push("/acl-settings");
            })
            .catch(err => {
                console.log(err);
                Notify.notify("Error updating ACL");
            });
    };

    if (pageLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" />
                <div>Loading ACL...</div>
            </div>
        );
    }

    return (
        <Card className="acl-card border-0 shadow-sm">
            <Card.Header className="acl-card-header">
                <div className="acl-header-icon">
                    <FaShieldAlt size={18} />
                </div>
                <div>
                    <h5 className="acl-header-title">Update ACL</h5>
                    <p className="acl-header-subtitle">Modify the access role and its module-level permissions</p>
                </div>
            </Card.Header>
            <Card.Body className="acl-card-body">

                <div className="acl-section">
                    <div className="acl-section-title">Basic Details</div>
                    <Form.Group className="mb-0">
                        <Form.Label>ACL Name</Form.Label>
                        <Form.Control
                            value={aclName}
                            onChange={(e) => setAclName(e.target.value)}
                        />
                    </Form.Group>
                </div>

                <Alert variant='warning' className="acl-alert">
                    <FaInfoCircle className="mt-1" />
                    <span>Only modules with View Permission appear in sidebar.</span>
                </Alert>

                <div className="acl-section">
                    <div className="acl-section-title">Module Permissions</div>

                    <InputGroup className="acl-search mb-3">
                        <InputGroup.Text className="acl-search-icon">
                            <FaSearch />
                        </InputGroup.Text>
                        <Form.Control
                            placeholder="Search module..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </InputGroup>

                    <div className="acl-table-wrapper">
                        <AclTable
                            loading={loading}
                            filteredModules={filteredModules}
                            permissions={permissions}
                            expandedModules={expandedModules}
                            moduleListingData={moduleListingData}
                            multiSelectOptions={multiSelectOptions}
                            togglePermission={togglePermission}
                            toggleAll={toggleAll}
                            isAllChecked={isAllChecked}
                            toggleExpand={toggleExpand}
                            setModuleListingData={setModuleListingData}
                        />
                    </div>
                </div>

            </Card.Body>

            <Card.Footer className="acl-card-footer">
                <Button onClick={handleUpdate} className="acl-save-btn">
                    <FaSave />
                    Update ACL
                </Button>
            </Card.Footer>
        </Card>
    );
};

export default UpdateAcl;